import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { useStore } from 'effector-react/ssr'
import { debounce } from 'debounce'
import ReactMarkdown from 'react-markdown'
import { Layout } from '@ui/layout'
import { paths } from '@pages/paths'
import { Devices } from '@styles'
import { EntityId } from '@typings'
import { matchMedia, MediaSource } from '@lib/image-media'
import newsCover from '@assets/images/news-1.jpg'
import newsCoverMobile from '@assets/images/news-cover-mobile.jpg'
import { isClientSide } from '@lib/ssr'
import { Social } from '@ui/social'
import * as model from './model'
import styles from './news.module.scss'
import { useBackgroundOnKroll } from '@lib/use-on-krol'

const newsMedia: MediaSource<string>[] = [
  { mediaQuery: Devices.laptop, source: newsCover },
  { mediaQuery: Devices.mobileS, source: newsCoverMobile },
]

export const NewsPage = () => {
  const [pageCover, setPageCover] = React.useState('')
  const showBackground = useBackgroundOnKroll()
  const history = useHistory()

  const newsList = useStore(model.$newsList)

  const onResize = React.useCallback(
    debounce(() => setPageCover(matchMedia(newsMedia, '')), 100),
    [],
  )

  React.useEffect(() => {
    if (isClientSide()) {
      setPageCover(matchMedia(newsMedia, ''))
      window.addEventListener('resize', onResize)
    }

    return () => {
      if (isClientSide()) {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [])

  const imageUrl = `url(${pageCover})`

  const navigateTo = (id: EntityId) => history.push(paths.newsById(id.toString()))

  return (
    <Layout className={styles.news} showBorder={showBackground}>
      <Social />
      <NewsCover style={{ backgroundImage: imageUrl }} />

      <NewsLayout>
        <PageTitle>Новости</PageTitle>

        {newsList.map((news) => (
          <NewsBlock key={news.id} onClick={() => navigateTo(news.id)}>
            <Preview src={news.preview} />
            <InfoBlock>
              <div>
                <Date>{news.date}</Date>
                <Title>{news.title}</Title>
              </div>
              <Description onClick={() => navigateTo(news.id)}>
                <ReactMarkdown>{news.description}</ReactMarkdown>
              </Description>
              <About to={paths.newsById(news.id.toString())}>Подробнее →</About>
            </InfoBlock>
          </NewsBlock>
        ))}
      </NewsLayout>
    </Layout>
  )
}

const NewsCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;

  width: 100vw;
  height: 100vh;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const NewsLayout = styled.div`
  @media screen and ${Devices.laptop} {
    padding-left: 175px;
  }
`

const PageTitle = styled.h1`
  font-family: 'Magnolia Script', 'Sans Serif';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 40px;

  margin: 0;

  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: 25px;

  @media screen and ${Devices.laptop} {
    font-size: 80px;
    line-height: 112px;

    margin-bottom: 40px;
    margin-left: 0;
  }
`

const NewsBlock = styled.div`
  display: flex;
  flex-direction: column;

  :hover {
    box-shadow: 4px 4px 22px rgba(0, 0, 0, 0.1);
  }
  margin: 25px;

  @media screen and ${Devices.laptop} {
    margin: 0;

    flex-direction: row;

    margin-bottom: 30px;

    width: 1070px;
    height: 266px;

    &:first-child {
      margin-top: 89px;
    }
  }
`

const InfoBlock = styled.div`
  display: flex;
  flex-wrap: wrap;

  background: white;
  padding: 30px 70px 22px 30px;
  @media (max-width: 800px) and (min-width: 300px) {
    padding: 20px 20px 25px 20px;
  }
`

const Date = styled.div`
  font-family: 'Roboto', 'SansSerif';
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  color: #828282;

  @media (max-width: 800px) and (min-width: 300px) {
    margin-bottom: 15px;
  }
`

const Title = styled.div`
  font-family: 'Raleway', 'SansSerif';
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 19px;

  @media (max-width: 800px) and (min-width: 300px) {
    margin-bottom: 5px;
  }
`

const Description = styled.div`
  margin-bottom: 25px;
  font-family: Raleway;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #000000;
`

const About = styled(Link)`
  font-family: Raleway;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #e469d1;
  text-decoration: none;
`

const Preview = styled.img`
  width: auto;
  @media (min-width: 1280px) {
    width: 470px;
    height: 266px;
  }
  @media (max-width: 800px) and (min-width: 300px) {
    width: 100%;
    height: 100%;
  }
`
